import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const Shell = ({ children, testId }) => (
  <section className="grid min-h-[80vh] place-items-center bg-[#141412] px-5 pb-20 pt-36 text-center text-[#FAF8F5] grain" data-testid={testId}>
    <div className="relative max-w-xl">{children}</div>
  </section>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: sid ? "checking" : "missing", kind: null, downloads: [] });

  useEffect(() => {
    if (!sid) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data?.payment_status === "paid") {
          if (data.kind === "order") clear();
          setState({ status: "paid", kind: data.kind, downloads: Array.isArray(data.downloads) ? data.downloads : [] });
          return;
        }
      } catch {
        // keep polling
      }
      tries += 1;
      if (tries < 10) timer = setTimeout(poll, 2000);
      else setState({ status: "pending", kind: null, downloads: [] });
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  if (state.status === "checking")
    return (
      <Shell testId="payment-checking">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#C59B27]" />
        <h1 className="mt-6 font-display text-4xl">Confirming your payment…</h1>
      </Shell>
    );
  if (state.status === "paid")
    return (
      <Shell testId="payment-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-[#C59B27]" />
        <h1 className="mt-6 font-display text-5xl">Thank you.</h1>
        <p className="mt-4 text-stone-300">
          {state.kind === "subscription"
            ? "Your catering subscription is active. Our team will contact you within one working day to book your site visit and tasting."
            : state.kind === "download"
              ? "Your purchase is complete. Download your PDFs below. We've also emailed you the download links so you can access them again at any time."
              : "Your Flapjack order is confirmed. A receipt is on its way to your email."}
        </p>
        {state.downloads.map((d) => (
          <a
            key={d.slug}
            href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/${sid}/${d.slug}`}
            className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-white/15 bg-white/5 p-5 text-left transition-colors hover:bg-white/10"
            data-testid={`download-link-${d.slug}`}
          >
            <span className="font-display text-xl">{d.name}</span>
            <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#C59B27] px-4 py-2 text-sm font-semibold text-[#141412]"><Download className="h-4 w-4" /> PDF</span>
          </a>
        ))}
        <Link to="/" className="btn-gold mt-10" data-testid="payment-home-link">Back to home</Link>
      </Shell>
    );
  return (
    <Shell testId="payment-pending">
      <h1 className="font-display text-4xl">{state.status === "missing" ? "No payment found" : "Payment still processing"}</h1>
      <p className="mt-4 text-stone-300">If you completed payment, you'll receive an email confirmation shortly.</p>
      <Link to="/" className="btn-gold mt-10" data-testid="payment-home-link">Back to home</Link>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell testId="payment-cancel">
    <XCircle className="mx-auto h-14 w-14 text-stone-400" />
    <h1 className="mt-6 font-display text-5xl">Payment cancelled</h1>
    <p className="mt-4 text-stone-300">No charge was made. You can try again whenever you're ready.</p>
    <Link to="/" className="btn-gold mt-10" data-testid="payment-cancel-home-link">Back to home</Link>
  </Shell>
);
